import { ReactNode } from 'react';
import { FieldHelpTooltip } from '@/Domain/components/Integration/components/FieldHelpTooltip';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';

interface IntegrationControlFieldProps {
  id: string;
  label: string;
  description?: ReactNode;
  /** Shown in a tooltip behind a help icon next to the label. */
  tooltip?: string;
  /** Optional element rendered inline next to the label (e.g. a "Saved" badge). */
  labelAddon?: ReactNode;
  /** The input or dropdown; rendered full-width inside its half of the row. */
  children: ReactNode;
}

/**
 * A standardized field row with the label (+ optional description) on the left
 * half and the input/dropdown on the right half, on one line. 14px label, 12px
 * description — matching the Account settings pattern.
 */
export const IntegrationControlField = ({
  id,
  label,
  description,
  tooltip,
  labelAddon,
  children,
}: IntegrationControlFieldProps) => (
  <Field orientation="horizontal" className="items-center gap-4">
    <FieldContent>
      <div className="flex items-center gap-2">
        <FieldLabel htmlFor={id} className="font-medium">
          {label}
        </FieldLabel>
        {tooltip ? <FieldHelpTooltip content={tooltip} data-testid={`${id}-help`} /> : null}
        {labelAddon}
      </div>
      {description ? <FieldDescription className="pb-0">{description}</FieldDescription> : null}
    </FieldContent>
    {/* Dropdown sizes to its content (min 100px) and sits at the right edge, mirroring the toggle's switch. */}
    <div className="[&>button]:w-fit [&>button]:min-w-[100px] [&>button]:max-w-full [&>button]:gap-2">
      {children}
    </div>
  </Field>
);

export default IntegrationControlField;
